import { Link } from 'react-router-dom';
import { isAdvisorWithoutAdmin } from '../../lib/cashFlowAccess';
import { useCanWrite } from '../../lib/useCanWrite';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { useAccessToken } from '../../auth/useAuth';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceKey } from '../../lib/workspaceQueryKey';
import { createApiClient } from '../../api/client';
import { levelFromRoles, longMonth, netCents, type SummaryResponse } from './cashFlowBoard';
import { dollars } from './Figures';
import { questionsQueryOptions } from './questionsQuery';
import { BOARD_MONTHS, useCashFlowBoard } from './useCashFlowBoard';

/**
 * Cash flow on Overview (steward-app#2596, docs/39 §7 decision 11): the Net cash flow card and the
 * Needs attention count. Pieces of the cash flow boards are reused, never copied: the viewer's
 * level (`levelFromRoles`), the summary read (`useCashFlowBoard`, so the card and the board share
 * ONE cache entry), the dollar format and the questions read.
 *
 * ## Who sees anything
 *
 * Only a viewer whose level is Totals by category or Every transaction, and never a firm advisor
 * standing in the family's workspace (`isAdvisorWithoutAdmin`): a firm sees nothing new on a
 * family's Overview. {@link NetCashFlowCard} decides that BEFORE mounting the body, so a viewer at
 * Nothing issues no cash flow read at all from Overview. Everything else renders nothing, with no
 * error and no empty card: cash flow off, nothing computed yet, a refusal, a failed load. The
 * board itself is where those states are explained. UX only: the façade and appcore enforce.
 */

/** Whether this viewer may see cash flow figures on Overview at all. */
export function useOverviewLevel() {
  const { activeMembership } = useWorkspace();
  const roleIds = activeMembership?.roleIds ?? [];
  if (!activeMembership || isAdvisorWithoutAdmin(roleIds)) return 'none' as const;
  return levelFromRoles(roleIds);
}

export function NetCashFlowCard() {
  if (useOverviewLevel() === 'none') return null;
  return <NetCashFlowBody />;
}

/** Overview reads the summary again on mount when the cached one is older than this (the app default is 30s). */
export const OVERVIEW_STALE_MS = 5000;

function NetCashFlowBody() {
  // A board answer or a correction queues a recompute that lands after the write returns, so a
  // summary cached a moment ago may predate it. Once, on mount: if the cached summary is older
  // than OVERVIEW_STALE_MS, read it again. (Younger than the app's 30s staleTime would otherwise
  // be served as is.) Not a poll, and no change to useCashFlowBoard, which the boards share.
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspace();
  useEffect(() => {
    const queryKey = workspaceKey('cash-flow-summary', activeWorkspaceId, 'all', BOARD_MONTHS);
    const st = queryClient.getQueryState(queryKey);
    if (st && st.fetchStatus === 'idle' && st.dataUpdatedAt > 0 && Date.now() - st.dataUpdatedAt > OVERVIEW_STALE_MS) {
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);
  const state = useCashFlowBoard<SummaryResponse>('cash-flow-summary', '/cash-flow/summary', 'all');
  if (state.kind !== 'ready' || state.data.status !== 'on') return null;
  const months = state.data.months;
  // "Last N months" counts every month Steward has an answer for (an empty one included: it is a
  // known zero). The latest month named is the newest one that holds a transaction, so a month
  // just begun does not read $0.
  const computed = months.filter(m => m.computed);
  const latest = [...computed].reverse().find(m => m.empty !== true);
  if (!latest || !latest.computed) return null;
  const inflow = computed.reduce((n, m) => n + (m.computed ? m.inflowCents : 0), 0);
  const outflow = computed.reduce((n, m) => n + (m.computed ? m.outflowCents : 0), 0);
  // One computed month: name it, and skip the "Latest month" figure (it would repeat the total).
  const single = computed.length === 1;
  const span = single ? longMonth(latest.month) : `Last ${computed.length} months`;
  const items: [string, string][] = [
    ['Inflows', dollars(inflow)],
    ['Outflows', dollars(outflow)],
    ...(single ? [] : [[`Latest month, ${longMonth(latest.month)}`, dollars(netCents(latest))] as [string, string]]),
  ];
  return (
    <Link
      to="/cash-flow"
      data-testid="net-cash-flow-card"
      className="block mb-10 bg-card border border-line rounded-token p-5 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bronze-strong"
    >
      <div className="font-serif text-base font-normal text-navy mb-1">Net cash flow</div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-slate font-sans mb-2">{span}</div>
      <div className="font-serif text-[28px] text-navy mb-4">{dollars(inflow - outflow)}</div>
      <dl className={`grid ${single ? "grid-cols-2" : "grid-cols-3"} max-sm:grid-cols-1 gap-4 m-0`}>
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[11px] uppercase tracking-[0.1em] text-slate font-sans">{label}</dt>
            <dd className="m-0 mt-1 font-sans text-[15px] text-navy">{value}</dd>
          </div>
        ))}
      </dl>
    </Link>
  );
}

/**
 * The open Worth a look count for the Needs attention row, or 0. Only for a viewer who sees every
 * transaction AND can answer (a read-only member would see a row they cannot act on). Reads the
 * same questions entry as Worth a look. `truncated` means "at least this many".
 */
export function useCashFlowQuestions(): { count: number; atLeast: boolean } {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();
  const level = useOverviewLevel();
  const canWrite = useCanWrite();
  const q = useQuery(questionsQueryOptions(createApiClient(token!), activeWorkspaceId,
    !!token && !!activeWorkspaceId && level === 'every' && canWrite));
  if (q.data?.status !== 'on') return { count: 0, atLeast: false };
  return { count: q.data.items.length, atLeast: q.data.truncated === true };
}
